import { useState } from "react";
import { UserRound, Cpu, Link2, LogOut, ChevronDown } from "lucide-react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from "@/components/ui/alert-dialog";

/**
 * Account menu.
 *
 * The header used to carry five equally weighted controls: site switcher,
 * alerts, AI settings, Google setup and disconnect. The last three shared one
 * identical class string, so nothing signalled which mattered. They are
 * low-frequency actions and now live behind one trigger.
 *
 * `settings-trigger`, `setup-guide-trigger` and `disconnect-button` keep their
 * exact ids on the menu items, so existing QA scripts still resolve. The new
 * `account-menu-trigger` opens the menu first.
 *
 * The dialogs themselves are owned by the dashboard and driven through
 * `onOpenSettings` / `onOpenSetup`. A Radix dialog trigger nested inside a
 * dropdown unmounts with the menu, so it would never open.
 *
 * @param {() => void} onOpenSettings
 * @param {() => void} onOpenSetup
 * @param {() => (void|Promise<void>)} onDisconnect
 * @param {boolean} [isDemo] Changes the disconnect wording: leaving a demo
 *   loses nothing, disconnecting a real account revokes data access.
 */
export default function AccountMenu({ onOpenSettings, onOpenSetup, onDisconnect, isDemo }) {
  const [confirming, setConfirming] = useState(false);

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            data-testid="account-menu-trigger"
            aria-label="Account and settings"
            className="tap-safe inline-flex items-center gap-1.5 rounded-btn border border-line bg-white px-3 text-small font-semibold text-navy-800 transition-colors duration-control ease-editorial hover:bg-offwhite focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 focus-visible:ring-offset-2 focus-visible:ring-offset-offwhite"
          >
            <UserRound size={16} aria-hidden="true" />
            <span className="hidden sm:inline">Account</span>
            <ChevronDown size={14} aria-hidden="true" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56 bg-white" data-testid="account-menu">
          <DropdownMenuLabel className="text-caption uppercase tracking-[0.08em] text-slate-600">
            Settings
          </DropdownMenuLabel>
          <DropdownMenuItem data-testid="settings-trigger" onSelect={() => onOpenSettings?.()} className="gap-2">
            <Cpu size={15} aria-hidden="true" /> AI settings
          </DropdownMenuItem>
          <DropdownMenuItem data-testid="setup-guide-trigger" onSelect={() => onOpenSetup?.()} className="gap-2">
            <Link2 size={15} aria-hidden="true" /> Set up Google
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            data-testid="disconnect-button"
            onSelect={() => setConfirming(true)}
            className="gap-2 text-red-600 focus:bg-red-50 focus:text-red-600"
          >
            <LogOut size={15} aria-hidden="true" /> {isDemo ? "Leave the demo" : "Disconnect Google"}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/*
        Disconnecting is not reversible in one click, so it gets a confirm step.
        The old header fired it immediately from a button that looked identical
        to "AI settings".
      */}
      <AlertDialog open={confirming} onOpenChange={setConfirming}>
        <AlertDialogContent data-testid="disconnect-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-h3 text-navy-950">
              {isDemo ? "Leave the demo?" : "Disconnect your Google account?"}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-small text-slate-600">
              {isDemo
                ? "You'll go back to the home page. Nothing is stored, so you can start the demo again any time."
                : "SEO Signals will stop reading your Search Console and Analytics data, and you'll be signed out. You can reconnect whenever you like."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="disconnect-cancel">Stay here</AlertDialogCancel>
            <AlertDialogAction
              data-testid="disconnect-confirm-button"
              onClick={() => onDisconnect?.()}
              className="bg-red-600 text-white hover:bg-red-600/90"
            >
              {isDemo ? "Leave" : "Disconnect"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
